"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toZonedDateTimeWithCustomFormat = exports.toRawZonedDateTime = exports.toZonedDateTime = exports.ZonedDateTime = exports.ZonedDateTimeConfig = exports.regExTypeDateTimeWithTZ = void 0;
const moment = require("moment-timezone");
const local_date_1 = require("./local-date");
const utils_1 = require("./utils");
exports.regExTypeDateTimeWithTZ = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3,6}))?([+-]\d{2}:{0,1}\d{2}|Z){0,1}\[(.+)\]$/;
const regExTimezone = /\[.+\]$/;
function extractDateAndTimezoneFromString(date) {
    if (date.includes('[') && exports.regExTypeDateTimeWithTZ.test(date)) {
        const tzMatch = new RegExp(exports.regExTypeDateTimeWithTZ).exec(date);
        if (tzMatch) {
            return {
                date: date.replace(regExTimezone, ''),
                tz: tzMatch[9]
            };
        }
    }
    return undefined;
}
exports.ZonedDateTimeConfig = {
    toStringFormatWithoutTimezone: undefined
};
/**
 * A ZonedDateTime represents a date-time with or without a time zone in the ISO-8601 calendar system.
 *
 * @example toZonedDateTime('2020-12-30T13:00:00.000Z')
 * @example toZonedDateTime(new Date('2020-12-30T13:00:00.000Z'))
 * @example ZonedDateTime.now(TIMEZONE)
 */
class ZonedDateTime extends local_date_1.LocalDate {
    constructor(_symbol, date, options) {
        super(_symbol, date, options);
        // @ts-ignore
        // note: we need to force incompatibility to the super-class
        // so it will not be possible to put LocalDate into ZonedDateTime
        Object.defineProperty(this, "__type__", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'datetime'
        });
        Object.defineProperty(this, "_timezone", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        let timezone = options === null || options === void 0 ? void 0 : options.timezone;
        const dateWithTZ = typeof date === 'string'
            ? extractDateAndTimezoneFromString(date)
            : undefined;
        if (dateWithTZ) {
            if (timezone && timezone !== dateWithTZ.tz) {
                console.warn('[ZonedDateTime]: Provided two different timezones, both in date-string and via parameter. Proceeding using the one provided via date-string.');
            }
            this.__originalDate__ = dateWithTZ.date;
            timezone = dateWithTZ.tz;
        }
        this._timezone = timezone;
    }
    get options() {
        return {
            format: this._format,
            locale: this._locale,
            timezone: this._timezone
        };
    }
    get moment() {
        return (0, utils_1.getMoment)(this.__originalDate__, this.options);
    }
    get timezone() {
        return this._timezone;
    }
    new(date, timezone) {
        return new ZonedDateTime(local_date_1.constructSymbol, date, Object.assign(Object.assign({}, this.options), { timezone: timezone || this.options.timezone }));
    }
    static now(timezone) {
        const now = moment();
        return toZonedDateTime(timezone ? now.tz(timezone) : now, timezone);
    }
    /**
     * Gets the clients timezone
     */
    static getLocalTZ() {
        return moment.tz.guess();
    }
    addMilliseconds(ms) {
        const newDate = this.moment.add(ms, 'ms');
        return this.new(newDate);
    }
    addSeconds(sec) {
        const newDate = this.moment.add(sec, 'seconds');
        return this.new(newDate);
    }
    addMinutes(minutes) {
        const newDate = this.moment.add(minutes, 'minutes');
        return this.new(newDate);
    }
    subMinutes(minutes) {
        const newDate = this.moment.subtract(minutes, 'minutes');
        return this.new(newDate);
    }
    subSeconds(seconds) {
        const newDate = this.moment.subtract(seconds, 'seconds');
        return this.new(newDate);
    }
    addHours(hours) {
        const newDate = this.moment.add(hours, 'hours');
        return this.new(newDate);
    }
    setHour(hour) {
        const newDate = this.moment.hour(hour);
        return this.new(newDate);
    }
    setMinute(minute) {
        const newDate = this.moment.minute(minute);
        return this.new(newDate);
    }
    setSecond(sec) {
        const newDate = this.moment.second(sec);
        return this.new(newDate);
    }
    setMillisecond(ms) {
        const newDate = this.moment.millisecond(ms);
        return this.new(newDate);
    }
    /**
     * Reset the timezone
     */
    setTZ(timezone) {
        return this.new(this.moment, timezone || undefined);
    }
    /**
     * Resets the timezone to the clients' local timezone
     */
    setLocalTZ() {
        return this.setTZ(ZonedDateTime.getLocalTZ());
    }
    getHours() {
        return this.moment.get('hour');
    }
    getMinutes() {
        return this.moment.get('minutes');
    }
    getSeconds() {
        return this.moment.get('seconds');
    }
    getMilliseconds() {
        return this.moment.get('milliseconds');
    }
    getTimezoneOffset() {
        return this.toDate().getTimezoneOffset();
    }
    isStartOfDay() {
        return (this.getHours() === 0 &&
            this.getMinutes() === 0 &&
            this.getSeconds() === 0);
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isToday() {
        return this.isSameDay(ZonedDateTime.now(this.timezone || ZonedDateTime.getLocalTZ()));
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isTomorrow() {
        return (this.diff(ZonedDateTime.now(this.timezone || ZonedDateTime.getLocalTZ()).startOf('day'), 'day') === 1);
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isYesterday() {
        return (this.diff(ZonedDateTime.now(this.timezone || ZonedDateTime.getLocalTZ()).endOf('day'), 'day') === -1);
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isFuture() {
        return (this.getTime() >
            ZonedDateTime.now(this.timezone || ZonedDateTime.getLocalTZ()).getTime());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isThisWeek() {
        return super.isThisWeek(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isNextWeek() {
        return super.isNextWeek(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isLastWeek() {
        return super.isLastWeek(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isThisMonth() {
        return super.isThisMonth(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isNextMonth() {
        return super.isNextMonth(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isLastMonth() {
        return super.isLastMonth(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * Converts `ZonedDateTime` to `Date`, while shallowly preserving the timezone for the client.
     * After working with the returned `Date`, please use `toZonedDateTime` to get it back into `ZonedDateTime`.
     */
    toDate() {
        const { moment } = this;
        const year = moment.get('year');
        const month = moment.get('month');
        const day = moment.get('date');
        const hour = moment.get('hour');
        const min = moment.get('minute');
        const sec = moment.get('second');
        const ms = moment.get('millisecond');
        return new Date(year, month, day, hour, min, sec, ms);
    }
    toString() {
        const { _timezone } = this;
        if (!_timezone &&
            exports.ZonedDateTimeConfig.toStringFormatWithoutTimezone === 'iso') {
            return this.moment.toISOString();
        }
        const parts = [moment(this.toDate()).format('YYYY-MM-DDTHH:mm:ss.SSS')];
        if (_timezone) {
            parts.push(this.format('Z'), `[${_timezone}]`);
        }
        return parts.join('');
    }
    toTimeString(withSeconds = false) {
        return withSeconds ? this.format('HH:mm:ss') : this.format('HH:mm');
    }
    toDayString() {
        return this.format('LL');
    }
}
exports.ZonedDateTime = ZonedDateTime;
function toZonedDateTime(date, appendTimezone) {
    if (!date) {
        return undefined;
    }
    const dateWithTZ = typeof date === 'string'
        ? extractDateAndTimezoneFromString(date)
        : undefined;
    const hasTimezoneInStr = Boolean(dateWithTZ === null || dateWithTZ === void 0 ? void 0 : dateWithTZ.tz);
    if (appendTimezone && !hasTimezoneInStr) {
        if (typeof date === 'string' && date.endsWith('Z')) {
            return toRawZonedDateTime(date).setTZ(appendTimezone);
        }
        const rawDate = toRawZonedDateTime(date);
        const offset = rawDate.toMoment().tz(appendTimezone).format('Z');
        if (rawDate.isValid()) {
            const toStringFormat = exports.ZonedDateTimeConfig.toStringFormatWithoutTimezone;
            if (toStringFormat) {
                exports.ZonedDateTimeConfig.toStringFormatWithoutTimezone = undefined;
            }
            const newDate = toRawZonedDateTime(`${rawDate.toString()}${offset}[${appendTimezone}]`);
            if (toStringFormat) {
                exports.ZonedDateTimeConfig.toStringFormatWithoutTimezone =
                    toStringFormat;
            }
            return newDate;
        }
        throw new Error(`[timezone] The provided date (${date}) is not valid. If you're passing a custom-format please use toZonedDateTimeWithCustomFormat`);
    }
    return new ZonedDateTime(local_date_1.constructSymbol, date);
}
exports.toZonedDateTime = toZonedDateTime;
/**
 * Converts a date (DateTimeVariant) to ZonedDateTime without applying the timezone
 */
function toRawZonedDateTime(date) {
    return toZonedDateTime(date, '');
}
exports.toRawZonedDateTime = toRawZonedDateTime;
/**
 * Converts a date-string to ZonedDateTime with a custom format
 */
function toZonedDateTimeWithCustomFormat(date, format) {
    return toRawZonedDateTime(date).setFormat(format);
}
exports.toZonedDateTimeWithCustomFormat = toZonedDateTimeWithCustomFormat;
